import { WIND_SHEETS, type WindSheet } from "./wind-presets.ts";

/**
 * The pointer the page reads to find the wind art (ADR-144's amendment of 2026-10-31): `assets/weather/current.json` under the
 * asset base lists each sheet by id with the path of its image, which carries the hash of its bytes. The pointer is JSON a
 * server hands the page, so every field is held to its kind and to the sheet the presets name.
 */

/**
 * One sheet in the pointer: the sheet's grid and where its image is under the asset base.
 */
export interface WindSheetEntry extends WindSheet {
  readonly path: string;
}

/**
 * The pointer's table: the sheets present, by id.
 */
export interface WindSheetPointer {
  readonly sheets: Readonly<Record<string, WindSheetEntry>>;
}

const PATH = /^assets\/weather\/[A-Za-z0-9][A-Za-z0-9._-]*\.png$/;
const FIELDS = ["path", "id", "frameWidth", "frameHeight", "columns", "frames"] as const;

/**
 * Checks the pointer.
 *
 * @param value The parsed `current.json`.
 * @returns The pointer.
 * @throws Error naming the first thing wrong: a pointer or entry that is not an object, a sheet no preset names, an entry whose
 *   grid is not the preset sheet's, a field it lacks or adds, or a path outside `assets/weather/`.
 */
export function validateWindSheets(value: unknown): WindSheetPointer {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error("the wind sheet pointer must be an object");
  const sheets = (value as Record<string, unknown>)["sheets"];
  if (typeof sheets !== "object" || sheets === null || Array.isArray(sheets)) throw new Error("the wind sheet pointer must hold a sheets object");
  const checked: Record<string, WindSheetEntry> = {};
  for (const [id, entry] of Object.entries(sheets)) {
    const known = WIND_SHEETS.find((sheet) => sheet.id === id);
    if (known === undefined) throw new Error(`${id} is not a wind sheet`);
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) throw new Error(`${id} must be an object`);
    const record = entry as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      if (!(FIELDS as readonly string[]).includes(key)) throw new Error(`${id}.${key} is not a field of a wind sheet`);
    }
    const path = record["path"];
    if (typeof path !== "string" || !PATH.test(path)) throw new Error(`${id}.path must be a PNG under assets/weather/`);
    for (const key of ["frameWidth", "frameHeight", "columns", "frames"] as const) {
      if (record[key] !== known[key]) throw new Error(`${id}.${key} must be ${known[key]}`);
    }
    checked[id] = { id, path, frameWidth: known.frameWidth, frameHeight: known.frameHeight, columns: known.columns, frames: known.frames };
  }
  return { sheets: checked };
}
